/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../../registry/Manager';
import { Platform } from '../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../registry/Component.type';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './CardActions';

const CardActionsManager: ComponentManagerFn<'CardActions'> = (
  platform: Platform,
): ComponentManager<'CardActions'> => {
  return {
    ...baseComponentManager<'CardActions'>(platform, 'CardActions'),
    defaultValue,
    defaultChildren: ['Button'],
    isChildAllowed: (page, item, childType) =>
      [
        ...getCustomChildItemTypes('CardActions'),
        'ActionButton',
        'Button',
        'ButtonDropdown',
        'ButtonGroup',
        'Toolbar',
      ].includes(childType),
    props: {
      disableSpacing: { label: 'Disable Spacing', type: 'boolean' },
    },
    name: 'Card Actions',
    canHaveChildren: () => true,
    category: 'Containers',
    icon: { iconName: 'io-card-actions', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Card Actions component acts as the dedicated footer for a Card, providing a standardized space for user interactions. Whether users need to "Save," "Cancel," or "Learn More," this container ensures that call-to-action buttons are presented consistently at the bottom of the card content.
By default, this component automatically centers all action items placed inside it, creating a balanced and professional appearance without requiring manual alignment adjustments.

**1. Action Layout & Spacing**<br/>
While the component handles the positioning of buttons automatically, you can fine-tune the relationship between multiple actions.
* **Disable Spacing:** Toggles the default margins between action items.
* **Standard Behavior (Off):** The component automatically adds breathing room between adjacent buttons to ensure they are distinct and clickable.
* **Grouped Behavior (On):** Removes the gap between items. Enable this when using button groups or toolbars where elements are visually designed to touch or merge.

**2. Content Guidelines**<br/>
The Card Actions container is strictly typed to ensure semantic correctness within the Card structure.
* **Placement:** It is designed to be a direct child of the Card component and will automatically anchor itself to the bottom of the card.
* **Allowed Content:** This container accepts actionable components only. You can add children such as: Button, Action Button, Button Dropdown, Button Group, and Toolbar.`,
  };
};

export default CardActionsManager;
